(() => {
  const SDK_POLL_INTERVAL = 100;
  const SDK_TIMEOUT = 5000;

  class WishlistService {
    constructor() {
      this._customerGid = window.theme?.customerId
        ? `gid://shopify/Customer/${window.theme.customerId}`
        : null;
      this._wishlistId = null;
      this._cache = new Map();
      this._writeQueue = Promise.resolve();
      this.isReady = false;

      if (!this._customerGid) {
        this._dispatchReady(false);
        return;
      }

      this._init();
    }

    async _init() {
      try {
        await this._waitForSdk();
        const response = await window.teifiWishlistApi.find({});
        const wishlists = response?.items ?? response ?? [];
        const wishlist = Array.isArray(wishlists) ? wishlists[0] : null;

        if (wishlist) {
          this._wishlistId = wishlist.id;
          this._populateCache(wishlist.items || []);
        }

        this._dispatchReady(true);
      } catch (e) {
        console.warn("WishlistService: Init failed", e);
        this._dispatchReady(true);
      }
    }

    _waitForSdk() {
      return new Promise((resolve, reject) => {
        const start = Date.now();
        const check = () => {
          if (window.teifiWishlistApi) {
            try {
              window.teifiWishlistApi.ready(resolve);
            } catch (e) {
              reject(e);
            }
            return;
          }
          if (Date.now() - start > SDK_TIMEOUT) {
            reject(new Error("Teifi SDK timeout"));
            return;
          }
          setTimeout(check, SDK_POLL_INTERVAL);
        };
        check();
      });
    }

    _populateCache(items) {
      this._cache.clear();
      items.forEach((item) => {
        if (item.handle) {
          this._cache.set(item.handle, {
            handle: item.handle,
            productGid: item.productGid,
            variantGid: item.variantGid,
            quantity: item.quantity || 1,
            note: item.note || "",
          });
        }
      });
    }

    has(handle) {
      return this._cache.has(handle);
    }

    getAll() {
      return Array.from(this._cache.values());
    }

    get count() {
      return this._cache.size;
    }

    add(itemOrHandle) {
      if (!this._customerGid) return;

      const item =
        typeof itemOrHandle === "string"
          ? { handle: itemOrHandle }
          : itemOrHandle;

      if (!item.handle || this._cache.has(item.handle)) return;

      const cacheEntry = {
        handle: item.handle,
        productGid: item.productId
          ? `gid://shopify/Product/${item.productId}`
          : null,
        variantGid: item.variantId
          ? `gid://shopify/ProductVariant/${item.variantId}`
          : null,
        quantity: 1,
        note: "",
      };

      this._cache.set(item.handle, cacheEntry);
      this._dispatchUpdate();

      this._enqueue(async () => {
        try {
          await this._syncToApi();
        } catch (e) {
          console.warn("WishlistService: Add failed", e);
          this._cache.delete(item.handle);
          this._dispatchUpdate();
        }
      });
    }

    remove(handle) {
      if (!this._customerGid || !this._cache.has(handle)) return;

      const removed = this._cache.get(handle);
      this._cache.delete(handle);
      this._dispatchUpdate();

      this._enqueue(async () => {
        try {
          await this._syncToApi();
        } catch (e) {
          console.warn("WishlistService: Remove failed", e);
          this._cache.set(handle, removed);
          this._dispatchUpdate();
        }
      });
    }

    clear() {
      if (!this._customerGid) return;

      const backup = new Map(this._cache);
      this._cache.clear();
      this._dispatchUpdate();

      this._enqueue(async () => {
        try {
          await this._syncToApi();
        } catch (e) {
          console.warn("WishlistService: Clear failed", e);
          for (const [k, v] of backup) this._cache.set(k, v);
          this._dispatchUpdate();
        }
      });
    }

    async _syncToApi() {
      const items = this.getAll().map((i) => this._toApiItem(i));
      const payload = {
        name: window.theme?.strings?.wishlistName || "My Wishlist",
        customerGid: this._customerGid,
        items,
      };
      if (!this._wishlistId) {
        const created = await window.teifiWishlistApi.create(payload);
        this._wishlistId = created.id;
      } else {
        await window.teifiWishlistApi.update(this._wishlistId, payload);
      }
    }

    _toApiItem(entry) {
      return {
        productGid: entry.productGid,
        variantGid: entry.variantGid,
        handle: entry.handle,
        quantity: entry.quantity || 1,
        note: entry.note || "",
      };
    }

    _enqueue(fn) {
      this._writeQueue = this._writeQueue.then(fn).catch(() => {});
    }

    _dispatchReady(loggedIn) {
      this.isReady = true;
      setTimeout(() => {
        window.dispatchEvent(
          new CustomEvent("wishlist:ready", { detail: { loggedIn } }),
        );
      }, 0);
    }

    _dispatchUpdate() {
      window.dispatchEvent(new CustomEvent("wishlist:updated"));
    }
  }

  window.wishlistService = new WishlistService();
})();
